import { useRef, useState } from 'react';
import { CustomTable, CustomTableRef } from 'medere-ui-framework';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { AccountDTO, AccountMovementListDTO } from '../../dto/CurrentAccountDTO';
import { useCurrentAccountService } from '../../servicesHooks/CurrentAccount/useCurrentAccountService';
import { formatDateOnly } from '../../utils/date';
import NvTooltips from '../../components/novarium/NvTooltips';
import MovementDetailDialog from './MovementDetailDialog';
import MovementAttachmentsDialog from './MovementAttachmentsDialog';
import { formatBalance, formatSignedMoney } from './currentAccountFormat';
import './CurrentAccount.scss';

interface AccountMovementsViewProps {
    account: AccountDTO;
    onBack: () => void;
}

/** Movimiento elegido para los adjuntos: el número de comprobante se muestra en el diálogo. */
interface AttachmentsTarget {
    accountMovementId: number;
    formNumber: string | null;
}

export default function AccountMovementsView({ account, onBack }: AccountMovementsViewProps) {
    const { getAccountMovements } = useCurrentAccountService();
    const tableRef = useRef<CustomTableRef>(null);
    const [detailMovementId, setDetailMovementId] = useState<number | null>(null);
    const [attachmentsTarget, setAttachmentsTarget] = useState<AttachmentsTarget | null>(null);

    const summary = [
        { label: 'Desde', value: account.fromentityname },
        { label: 'Hacia', value: account.toentityname },
        { label: 'Saldo actual', value: formatBalance(account.currentbalance) },
    ];

    return (
        <div className="nv-scope nv-page current-account-page">
            <NvTooltips />

            <header className="nv-page-head">
                <Button type="button" link icon="pi pi-angle-double-left" label="Volver" className="nv-back" onClick={onBack} />
                <h1 className="nv-h1">Movimientos de cuenta corriente</h1>
                <span className="nv-subtitle">Cuenta N.° {account.account}</span>
            </header>

            <section className="nv-card" aria-label="Resumen de la cuenta">
                <dl className="nv-summary-grid">
                    {summary.map(item => (
                        <div key={item.label} className="nv-ro">
                            <dt className="nv-ro-label">{item.label}</dt>
                            <dd className="nv-ro-value nv-strong nv-truncate">{item.value || '—'}</dd>
                        </div>
                    ))}
                </dl>
            </section>

            <CustomTable
                idTable="tableAccountMovements"
                ref={tableRef}
                getItems={getAccountMovements}
                className="nv-load-table"
                containerClassName="current-account-page__table"
                filterRow={false}
                hideFiltersButton
                params={{ accountId: account.account }}
            >
                <Column
                    header="Fecha"
                    body={(row: AccountMovementListDTO) => formatDateOnly(row.movementdate)}
                    className="nv-col-date"
                />
                <Column
                    header="Comprobante"
                    body={(row: AccountMovementListDTO) => row.formnumberdisplay || '—'}
                    className="nv-col-status"
                />
                <Column
                    header="Descripción"
                    body={(row: AccountMovementListDTO) => <span className="nv-truncate">{row.description || '—'}</span>}
                    className="nv-col-item"
                />
                <Column
                    header="Importe"
                    body={(row: AccountMovementListDTO) => formatSignedMoney(row.amount)}
                    align="right"
                    alignHeader="right"
                    className="nv-col-money"
                />
                <Column
                    header="Saldo"
                    body={(row: AccountMovementListDTO) => <span className="nv-strong">{formatBalance(row.balance)}</span>}
                    align="right"
                    alignHeader="right"
                    className="nv-col-money"
                />
                <Column
                    className="nv-col-actions"
                    body={(row: AccountMovementListDTO) => (
                        <div className="nv-actions-end">
                            <Button
                                type="button"
                                text
                                className="nv-icon nv-tip"
                                icon="pi pi-search"
                                aria-label="Ver detalle del movimiento"
                                data-pr-tooltip="Ver detalle"
                                onClick={() => setDetailMovementId(row.accountmovement)}
                            />
                            <Button
                                type="button"
                                text
                                className="nv-icon nv-tip"
                                icon="pi pi-paperclip"
                                aria-label="Ver comprobantes adjuntos"
                                data-pr-tooltip="Comprobantes adjuntos"
                                onClick={() => setAttachmentsTarget({
                                    accountMovementId: row.accountmovement,
                                    formNumber: row.formnumberdisplay,
                                })}
                            />
                        </div>
                    )}
                />
            </CustomTable>

            <MovementDetailDialog
                accountMovementId={detailMovementId}
                onHide={() => setDetailMovementId(null)}
            />

            <MovementAttachmentsDialog
                accountMovementId={attachmentsTarget?.accountMovementId ?? null}
                formNumber={attachmentsTarget?.formNumber ?? null}
                onHide={() => setAttachmentsTarget(null)}
            />
        </div>
    );
}
